/*
  Click-to-load video dialog.

  Nothing about the video is fetched on page load — no <video> element, no
  poster, no preload. The player is built on the first click and torn down on
  close, so a visitor who never clicks pays nothing for it.
*/

class BeeVideo extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.frame = this.querySelector('[data-bee-video-frame]');
    this.spinner = this.querySelector('[data-bee-video-spinner]');
    this.video = null;

    if (!this.dialog || !this.frame) return;

    this.dialog.addEventListener('close', this.teardown.bind(this));
    this.dialog.addEventListener('click', this.onDialogClick.bind(this));
    this.querySelectorAll('[data-bee-video-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    // Delegated, so triggers in sections rendered later — or re-rendered by the
    // theme editor — keep working without rebinding.
    this.onDocumentClick = (event) => {
      const trigger = event.target.closest('[data-bee-video-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
  }

  get src() {
    return this.dataset.videoSrc;
  }

  open(trigger) {
    if (!this.src) return;

    // Older browsers without the top-layer dialog: just hand them the file.
    if (typeof this.dialog.showModal !== 'function') {
      window.open(this.src, '_blank', 'noopener');
      return;
    }

    this.trigger = trigger;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (!this.video) this.video = this.build();

    this.dialog.showModal();
    document.body.classList.add('overflow-hidden');

    // The click is a user gesture, so sound is allowed. If the browser still
    // refuses, the controls are right there.
    const played = this.video.play();
    if (played) played.catch(() => {});
  }

  build() {
    const video = document.createElement('video');
    video.className = 'bee-video-dialog__player';
    video.controls = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('controlsList', 'nodownload');
    if (this.dataset.videoPoster) video.poster = this.dataset.videoPoster;

    const source = document.createElement('source');
    source.src = this.src;
    source.type = this.dataset.videoType || 'video/mp4';
    video.appendChild(source);

    if (this.dataset.videoCaptions) {
      const track = document.createElement('track');
      track.kind = 'captions';
      track.src = this.dataset.videoCaptions;
      track.srclang = this.dataset.videoCaptionsLang || 'en';
      track.label = this.dataset.videoCaptionsLabel || 'English';
      track.default = true;
      video.appendChild(track);
    }

    if (this.spinner) {
      const hideSpinner = () => this.spinner.setAttribute('hidden', '');
      video.addEventListener('playing', hideSpinner, { once: true });
      video.addEventListener('error', hideSpinner, { once: true });
    }

    this.frame.appendChild(video);
    return video;
  }

  onDialogClick(event) {
    // Clicks land on the dialog itself only when they hit the backdrop.
    if (event.target === this.dialog) this.dialog.close();
  }

  teardown() {
    document.body.classList.remove('overflow-hidden');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');

    // Dropping the element cancels any in-flight download.
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.video.remove();
      this.video = null;
    }
    if (this.spinner) this.spinner.removeAttribute('hidden');
  }
}

customElements.define('bee-video', BeeVideo);
